// syncMode.js — v0.171.0 — SITE MODE: save on the phone, push when you choose.
// ---------------------------------------------------------------------------
// Brett, 2026-09-14: "Droppin is too slow and photos don't look like they are
// saving from site. Can you make it offline then push all at once when I'm
// connected to proper wifi."
//
// WHAT WAS HAPPENING. Every save on the plan tried the NETWORK FIRST and only
// fell back to the outbox when the request failed outright. On one bar of
// reception a request does not fail — it hangs, for as long as the phone is
// willing to wait — so Save sat on "Saving…" and the photos behind it uploaded
// one multi-megabyte JSON body at a time, in the background, with nothing on
// the pin saying they had not gone yet.
//
// SITE MODE is a per-device setting. When it is on:
//   • plan-screen writes (pins, photos, comments) go STRAIGHT to the outbox —
//     no network attempt, so Save is instant on site and never waits on a
//     hung request;
//   • nothing is sent until the user taps "Sync now", or (where the device
//     can tell — Android/Chrome) it is on Wi-Fi and "auto-sync on Wi-Fi" is
//     on. iOS does not expose the connection type, so there it is the tap.
//
// When it is off ('auto') behaviour is as before: send immediately, queue
// only when the network fails, drain on reconnect.
//
// Pure decision logic is exported separately so it can be unit-tested in
// node without touching localStorage or navigator.

const MODE_KEY      = 'droppin.sync.mode.v1';      // 'auto' | 'hold'
const AUTO_WIFI_KEY = 'droppin.sync.autoWifi.v1';  // '1' | '0'

export const SYNC_MODES = ['auto', 'hold'];

const _listeners = new Set();
function notify() { _listeners.forEach((fn) => { try { fn(); } catch { /* listener's problem */ } }); }
export function subscribeSyncMode(fn) {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}

function readLs(key) {
  try { return typeof localStorage === 'undefined' ? null : localStorage.getItem(key); }
  catch { return null; }
}
function writeLs(key, value) {
  try { if (typeof localStorage !== 'undefined') localStorage.setItem(key, value); }
  catch { /* private mode / quota — the setting simply does not persist */ }
}

export function getSyncMode() {
  const v = readLs(MODE_KEY);
  return SYNC_MODES.includes(v) ? v : 'auto';
}

export function setSyncMode(mode) {
  const m = SYNC_MODES.includes(mode) ? mode : 'auto';
  writeLs(MODE_KEY, m);
  notify();
}

export function isHoldMode() { return getSyncMode() === 'hold'; }

export function getAutoWifi() {
  const v = readLs(AUTO_WIFI_KEY);
  return v == null ? true : v === '1';
}

export function setAutoWifi(on) {
  writeLs(AUTO_WIFI_KEY, on ? '1' : '0');
  notify();
}

// The connection type as the browser reports it, or null when it will not
// say. Only the Network Information API exposes this (Android Chrome and
// friends); iOS Safari has no such thing, and null there is the honest
// answer — never guess "wifi" from a fast effectiveType.
export function connectionType() {
  try {
    const c = typeof navigator !== 'undefined'
      ? (navigator.connection || navigator.mozConnection || navigator.webkitConnection)
      : null;
    const t = c && typeof c.type === 'string' ? c.type : null;
    return t || null;
  } catch { return null; }
}

// ── Pure decision: should the app send the queue on its own right now? ──
//
//   mode           'auto' | 'hold'
//   online         navigator.onLine
//   connection     connectionType()  — 'wifi' | 'cellular' | … | null (unknown)
//   autoWifi       the "auto-sync on Wi-Fi" preference
//
// 'auto' mode drains whenever online (today's behaviour). 'hold' mode drains
// on its own ONLY when the device positively reports Wi-Fi and the user has
// left auto-sync on. An unknown connection never counts as Wi-Fi: on a phone
// that cannot say, "hold" means hold, and the Sync now button is the release.
export function shouldAutoSync({ mode, online, connection, autoWifi }) {
  if (!online) return false;
  if (mode !== 'hold') return true;
  return !!autoWifi && connection === 'wifi';
}

// Convenience for the live app.
export function autoSyncAllowedNow() {
  return shouldAutoSync({
    mode: getSyncMode(),
    online: typeof navigator === 'undefined' ? true : navigator.onLine,
    connection: connectionType(),
    autoWifi: getAutoWifi(),
  });
}
